import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Dumbbell, Utensils, TrendingUp, Sparkles, LayoutDashboard, Lightbulb, LogOut, Menu, X, Crown, Scale, User, ChevronDown, Trophy, Target, Moon, Medal, Watch } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/exercises", label: "Øvelser", icon: Dumbbell },
  { to: "/training", label: "Træning", icon: TrendingUp },
  { to: "/meals", label: "Madplaner", icon: Utensils },
  { to: "/ai-coach", label: "AI Coach", icon: Sparkles },
  { to: "/body-tracker", label: "Krop", icon: Scale },
];

export default function Navbar() {
  const { user, logout, subscription } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <div className="w-9 h-9 rounded-lg bg-[#DFFF00] flex items-center justify-center group-hover:shadow-[0_0_20px_rgba(223,255,0,0.3)] transition-shadow">
              <Dumbbell className="h-5 w-5 text-black" />
            </div>
            <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase tracking-wider text-white hidden sm:block">
              Fitrack<span className="text-[#DFFF00]">R</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1 bg-zinc-900/50 rounded-full p-1 border border-white/5">
            {NAV_LINKS.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to} data-testid={`nav-${to.slice(1)}`}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-full transition-all duration-200 ${
                  location.pathname === to 
                    ? "bg-[#DFFF00] text-black font-bold" 
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}>
                <Icon className="h-4 w-4" />
                <span className="hidden xl:inline">{label}</span>
              </Link>
            ))}
          </div>

          {/* User Menu */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Upgrade Button */}
            {!subscription?.subscription_active && (
              <Link to="/upgrade">
                <Button className="bg-gradient-to-r from-[#DFFF00] to-[#9EFF00] text-black hover:shadow-[0_0_20px_rgba(223,255,0,0.3)] rounded-full font-bold text-xs uppercase tracking-wider h-9 px-4">
                  <Crown className="h-3.5 w-3.5 mr-1" /> Pro
                </Button>
              </Link>
            )}
            
            {/* User Dropdown */}
            <div className="relative">
              <button 
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 bg-zinc-900/50 border border-white/5 rounded-full pl-3 pr-2 py-1.5 hover:border-white/10 transition-colors"
                data-testid="nav-user-menu"
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#DFFF00]/20 to-[#DFFF00]/5 flex items-center justify-center">
                  <span className="text-xs font-bold text-[#DFFF00]">{user?.name?.charAt(0).toUpperCase()}</span>
                </div>
                <span className="text-sm text-zinc-300 max-w-24 truncate">{user?.name}</span>
                <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-[#18181b] border border-white/10 rounded-xl shadow-xl overflow-hidden">
                  <Link to="/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:bg-white/5 transition-colors">
                    <User className="h-4 w-4" /> Min Profil
                  </Link>
                  <Link to="/achievements" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:bg-white/5 transition-colors">
                    <Trophy className="h-4 w-4 text-yellow-500" /> Achievements
                  </Link>
                  <Link to="/goals" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:bg-white/5 transition-colors">
                    <Target className="h-4 w-4 text-[#DFFF00]" /> Mål
                  </Link>
                  <Link to="/leaderboard" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:bg-white/5 transition-colors">
                    <Medal className="h-4 w-4 text-orange-500" /> Leaderboard
                  </Link>
                  <Link to="/sleep" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:bg-white/5 transition-colors">
                    <Moon className="h-4 w-4 text-purple-400" /> Søvn
                  </Link>
                  <Link to="/inspiration" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-300 hover:bg-white/5 transition-colors">
                    <Lightbulb className="h-4 w-4" /> Inspiration
                  </Link>
                  <div className="border-t border-white/5" />
                  <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-3 text-sm text-red-400 hover:bg-red-500/10 transition-colors w-full" data-testid="nav-logout">
                    <LogOut className="h-4 w-4" /> Log ud
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Toggle */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-white p-2" data-testid="nav-mobile-toggle">
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden py-4 border-t border-white/5 space-y-1">
            {NAV_LINKS.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to} onClick={() => setMobileOpen(false)} data-testid={`nav-mobile-${to.slice(1)}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === to 
                    ? "bg-[#DFFF00]/10 text-[#DFFF00]" 
                    : "text-zinc-400 hover:bg-white/5"
                }`}>
                <Icon className="h-5 w-5" />
                <span className="font-medium">{label}</span>
              </Link>
            ))}
            <div className="border-t border-white/5 my-2" />
            <Link to="/profile" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:bg-white/5 rounded-lg">
              <User className="h-5 w-5" /> Min Profil
            </Link>
            <Link to="/achievements" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:bg-white/5 rounded-lg">
              <Trophy className="h-5 w-5 text-yellow-500" /> Achievements
            </Link>
            <Link to="/goals" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:bg-white/5 rounded-lg">
              <Target className="h-5 w-5 text-[#DFFF00]" /> Mål
            </Link>
            <Link to="/leaderboard" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:bg-white/5 rounded-lg">
              <Medal className="h-5 w-5 text-orange-500" /> Leaderboard
            </Link>
            <Link to="/sleep" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:bg-white/5 rounded-lg">
              <Moon className="h-5 w-5 text-purple-400" /> Søvn
            </Link>
            <Link to="/inspiration" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:bg-white/5 rounded-lg">
              <Lightbulb className="h-5 w-5" /> Inspiration
            </Link>
            {!subscription?.subscription_active && (
              <Link to="/upgrade" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 text-[#DFFF00] hover:bg-[#DFFF00]/10 rounded-lg">
                <Crown className="h-5 w-5" /> Opgrader til Pro
              </Link>
            )}
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-lg w-full" data-testid="nav-mobile-logout">
              <LogOut className="h-5 w-5" /> Log ud
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
